"use client";

import { useState } from "react";
import {
  type BoxGroup,
  BoxPlotChart,
} from "@/components/tool-kit/charts/box-plot-chart";
import {
  ChartStyleFields,
  sortOptions,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  Checkbox,
  DataTable,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import type { QuantileMethod } from "@/lib/stats/descriptive";
import { type SortOrder, sortIndices } from "@/lib/tool-kit/charts/bars";
import { boxStats } from "@/lib/tool-kit/charts/box";
import {
  type NumberFormat,
  usesDecimalComma,
  valueText,
} from "@/lib/tool-kit/charts/format";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  type Groups,
  parseColumnGroups,
  parseRowGroups,
} from "@/lib/tool-kit/charts/parse";

/** English strings. A translated content file overrides these through `ui`. */
export const BOX_PLOT_MAKER_STRINGS = {
  dataLabel: "Your data",
  dataHintColumns:
    "One group per column, with the group names in the first row — paste straight from a spreadsheet. Separate with commas, tabs or semicolons (semicolons if your numbers have decimal commas).",
  dataHintRows:
    "One group per line: the name, a colon, then the values — Morning: 72, 75, 78. Lines without a name are numbered.",
  exampleColumns:
    "Morning, Afternoon, Evening\n72, 65, 58\n75, 70, 62\n78, 68, 66\n80, 74, 61\n82, 77, 70\n85, 71, 64\n88, 79, 59\n90, 73, 68\n76, 81, 95\n84, 69, 63",
  exampleRows:
    "Morning: 72, 75, 78, 80, 82, 85, 88, 90, 76, 84\nAfternoon: 65, 70, 68, 74, 77, 71, 79, 73, 81, 69\nEvening: 58, 62, 66, 61, 70, 64, 59, 68, 95, 63",
  firstLineHeader: "The first line names the groups",
  exampleTitle: "Quiz Scores by Class Section",
  exampleGroupTitle: "Class section",
  exampleValueTitle: "Quiz score",
  layoutLabel: "Data layout",
  layoutColumns: "One group per column",
  layoutRows: "One group per line",
  methodLabel: "Quartile method",
  methodTukey: "Median of each half (TI-84, most textbooks)",
  methodInclusive: "Inclusive (Excel QUARTILE.INC, R, NumPy)",
  methodExclusive: "Exclusive (Excel QUARTILE.EXC, SPSS, Minitab)",
  orientationLabel: "Boxes",
  horizontal: "Horizontal",
  vertical: "Vertical",
  showMean: "Mark the mean with a diamond",
  sortLabel: "Order of the groups",
  sortNone: "As entered",
  sortAscending: "Lowest median first",
  sortDescending: "Highest median first",
  legendMean: "Mean",
  legendOutlier: "Outlier",
  summaryHeading: "Five-number summary",
  groupColumn: "Group",
  nColumn: "n",
  minColumn: "Min",
  q1Column: "Q1",
  medianColumn: "Median",
  q3Column: "Q3",
  maxColumn: "Max",
  iqrColumn: "IQR",
  fencesColumn: "Fences",
  outliersColumn: "Outliers",
  fenceRange: "{low} to {high}",
  noOutliers: "None",
  whiskerNote:
    "Whiskers reach the most extreme values within the fences, Q1 − 1.5 × IQR and Q3 + 1.5 × IQR. Values beyond a fence are drawn on their own as outliers.",
  groupDefault: "Group {number}",
  groupsDetected: "one:{count} group|other:{count} groups",
  labelsAndStyle: "Labels and style",
  chartTitle: "Chart title",
  xAxisTitle: "Group axis title",
  yAxisTitle: "Value axis title",
  axisTitlePlaceholderX: "Group",
  axisTitlePlaceholderY: "Value",
  colorLabel: "Box color",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  valueLabels: "Show the median on each box",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and the gridlines out of the plot, and sets the figure number in bold and the title in italics above the chart — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy summary table",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  chartLabel: "Box plot",
  chartDescription: "Box plot of {groups}.",
  groupDescription:
    "{name}: median {median}, quartiles {q1} and {q3}, whiskers from {low} to {high}.",
  enterData: "Enter at least one group of numbers to draw a box plot.",
  smallGroups:
    "one:{names} has fewer than five values, so its quartiles rest on very little data.|other:{names} have fewer than five values each, so their quartiles rest on very little data.",
  oneValueColumns:
    "Every column holds a single value. If each group is on its own line, choose “One group per line”.",
  invalidEntries:
    "one:{count} entry is not a number and was left out.|other:{count} entries are not numbers and were left out.",
} as const;

type Strings = typeof BOX_PLOT_MAKER_STRINGS;
type Layout = "columns" | "rows";

/** Groups with their statistics, named, in display order. */
function prepareGroups(
  parsed: Groups,
  method: QuantileMethod,
  sort: SortOrder,
  strings: Strings,
  fmt: NumberFormat
): BoxGroup[] {
  const groups: BoxGroup[] = [];
  for (const [index, values] of parsed.groups.entries()) {
    const stats = boxStats(values, method);
    if (stats) {
      const name =
        parsed.names[index] ??
        t(strings.groupDefault, { number: fmt(index + 1) });
      groups.push({ name, stats });
    }
  }
  return sortIndices(
    groups.map((g) => g.stats.median),
    sort
  ).map((i) => groups[i]);
}

function summaryRows(
  groups: BoxGroup[],
  strings: Strings,
  fmt: NumberFormat
): string[][] {
  const number = (value: number) => valueText(fmt, value, 3);
  const separator = usesDecimalComma(fmt) ? "; " : ", ";
  return groups.map(({ name, stats }) => [
    name,
    fmt(stats.n),
    number(stats.min),
    number(stats.q1),
    number(stats.median),
    number(stats.q3),
    number(stats.max),
    number(stats.iqr),
    t(strings.fenceRange, {
      low: number(stats.lowerFence),
      high: number(stats.upperFence),
    }),
    stats.outliers.length > 0
      ? stats.outliers.map(number).join(separator)
      : strings.noOutliers,
  ]);
}

function boxNotices(
  parsed: Groups,
  groups: BoxGroup[],
  layout: Layout,
  strings: Strings,
  locale: string,
  fmt: NumberFormat
): string[] {
  const notes: string[] = [];
  if (groups.length === 0) {
    notes.push(strings.enterData);
  }
  if (
    layout === "columns" &&
    groups.length > 1 &&
    groups.every((g) => g.stats.n === 1)
  ) {
    notes.push(strings.oneValueColumns);
  }
  const small = groups.filter((g) => g.stats.n < 5);
  if (
    small.length > 0 &&
    !(
      layout === "columns" &&
      small.length === groups.length &&
      groups.length > 1
    )
  ) {
    notes.push(
      tPlural(strings.smallGroups, locale, small.length, {
        names: small.map((g) => g.name).join(", "),
      })
    );
  }
  if (parsed.invalid > 0) {
    notes.push(
      tPlural(strings.invalidEntries, locale, parsed.invalid, {
        count: fmt(parsed.invalid),
      })
    );
  }
  return notes;
}

export default function BoxPlotMakerWidget() {
  const strings = useToolUi(BOX_PLOT_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [layout, setLayout] = useState<Layout>("columns");
  const [raw, setRaw] = useState<string>(strings.exampleColumns);
  const [method, setMethod] = useState<QuantileMethod>("tukey");
  const [horizontal, setHorizontal] = useState(true);
  const [showMean, setShowMean] = useState(false);
  const [sort, setSort] = useState<SortOrder>("none");
  const [headerChoice, setHeaderChoice] = useState<boolean | null>(null);
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: strings.exampleGroupTitle,
    yTitle: strings.exampleValueTitle,
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  function changeLayout(next: Layout) {
    // Swap the example along with the layout, but never the reader's own data.
    const current =
      layout === "columns" ? strings.exampleColumns : strings.exampleRows;
    if (raw === current) {
      setRaw(next === "columns" ? strings.exampleColumns : strings.exampleRows);
    }
    setLayout(next);
  }

  const parsed =
    layout === "columns"
      ? parseColumnGroups(raw, { header: headerChoice ?? undefined })
      : parseRowGroups(raw);
  const groups = prepareGroups(parsed, method, sort, strings, fmt);
  const rows = summaryRows(groups, strings, fmt);
  const head = [
    strings.groupColumn,
    strings.nColumn,
    strings.minColumn,
    strings.q1Column,
    strings.medianColumn,
    strings.q3Column,
    strings.maxColumn,
    strings.iqrColumn,
    strings.fencesColumn,
    strings.outliersColumn,
  ];
  const number = (value: number) => valueText(fmt, value, 3);
  const description = [
    t(strings.chartDescription, {
      groups: tPlural(strings.groupsDetected, locale, groups.length, {
        count: fmt(groups.length),
      }),
    }),
    ...groups.map(({ name, stats }) =>
      t(strings.groupDescription, {
        name,
        median: number(stats.median),
        q1: number(stats.q1),
        q3: number(stats.q3),
        low: number(stats.whiskerLow),
        high: number(stats.whiskerHigh),
      })
    ),
  ].join(" ");

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.layoutLabel}
        name="bpm-layout"
        onChange={changeLayout}
        options={[
          { value: "columns", label: strings.layoutColumns },
          { value: "rows", label: strings.layoutRows },
        ]}
        value={layout}
      />
      <TextAreaField
        hint={
          layout === "columns" ? strings.dataHintColumns : strings.dataHintRows
        }
        id="bpm-data"
        label={strings.dataLabel}
        onChange={setRaw}
        rows={8}
        value={raw}
      />
      {layout === "columns" ? (
        <Checkbox
          checked={headerChoice ?? parsed.autoHeader}
          label={strings.firstLineHeader}
          onChange={setHeaderChoice}
        />
      ) : null}
      <div aria-live="polite" className="space-y-2">
        {groups.length > 0 ? (
          <p className={HINT}>
            {tPlural(strings.groupsDetected, locale, groups.length, {
              count: fmt(groups.length),
            })}
          </p>
        ) : null}
        {boxNotices(parsed, groups, layout, strings, locale, fmt).map(
          (notice) => (
            <Notice key={notice}>{notice}</Notice>
          )
        )}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="bpm-method"
          label={strings.methodLabel}
          onChange={setMethod}
          options={[
            { value: "tukey", label: strings.methodTukey },
            { value: "inclusive", label: strings.methodInclusive },
            { value: "exclusive", label: strings.methodExclusive },
          ]}
          value={method}
        />
        <PillGroup
          label={strings.orientationLabel}
          name="bpm-orientation"
          onChange={(value) => setHorizontal(value === "horizontal")}
          options={[
            { value: "horizontal", label: strings.horizontal },
            { value: "vertical", label: strings.vertical },
          ]}
          value={horizontal ? "horizontal" : "vertical"}
        />
      </div>
      <Checkbox
        checked={showMean}
        label={strings.showMean}
        onChange={setShowMean}
      />
      {groups.length > 0 ? (
        <ChartFigure
          apa={style.apa}
          data={toTsv([head, ...rows])}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "box-plot")}
          labels={strings}
          note={style.note}
          table={null}
          title={style.title}
        >
          <BoxPlotChart
            description={description}
            fmt={fmt}
            groups={groups}
            horizontal={horizontal}
            id="bpm-chart"
            label={style.title.trim() || strings.chartLabel}
            legendLabels={strings}
            showMean={showMean}
            style={style}
          />
        </ChartFigure>
      ) : null}
      {groups.length > 0 ? (
        <section aria-live="polite" className="space-y-3">
          <h2 className={WIDGET_H2}>{strings.summaryHeading}</h2>
          <DataTable head={head} rows={rows} />
          <p className={HINT}>{strings.whiskerNote}</p>
        </section>
      ) : null}
      <ChartStyleFields
        axisLabels={strings}
        colorLabels={strings}
        idPrefix="bpm"
        labels={strings}
        style={style}
        update={update}
        valueLabel={strings.valueLabels}
      >
        <SelectField
          id="bpm-sort"
          label={strings.sortLabel}
          onChange={setSort}
          options={sortOptions(strings)}
          value={sort}
        />
      </ChartStyleFields>
    </div>
  );
}
